import { createSlice } from '@reduxjs/toolkit';
import { fetchUserFacilities, fetchFacilities } from '../thunks/facilitiesThunks';

/**
 * Sanitize facility data to ensure Redux serialization compatibility
 * @param {any} data - Facility data to sanitize
 * @returns {any} Sanitized facility data
 */
const sanitizeFacilityData = (data) => {
  if (!data || typeof data !== 'object') return data;
  
  const sanitized = { ...data };
  
  Object.keys(sanitized).forEach(key => {
    const value = sanitized[key];
    
    // Check if value is a Firestore Timestamp
    if (value && typeof value === 'object' && value._seconds !== undefined && value._nanoseconds !== undefined) {
      // Convert Timestamp to ISO string for Redux serialization
      const date = new Date(value._seconds * 1000 + value._nanoseconds / 1000000);
      sanitized[key] = date.toISOString();
    } 
    // Handle Date objects - convert to ISO string for Redux serialization
    else if (value instanceof Date) {
      sanitized[key] = value.toISOString();
    }
    // Handle nested objects and arrays
    else if (value && typeof value === 'object') {
      if (Array.isArray(value)) {
        sanitized[key] = value.map(item => 
          typeof item === 'object' ? sanitizeFacilityData(item) : item
        );
      } else {
        sanitized[key] = sanitizeFacilityData(value);
      }
    }
  });
  
  return sanitized;
};

const initialState = {
  user: null,
  role: null,
  loading: false,
  error: null,
  loginComplete: false,
  lastAuthUpdate: null, // Track when auth was last updated for security
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action) => {
      // Sanitize user data to ensure Redux serialization compatibility
      const sanitizedUser = action.payload ? {
        ...action.payload,
        // Ensure defaultFacility is sanitized if it exists
        defaultFacility: action.payload.defaultFacility ? sanitizeFacilityData(action.payload.defaultFacility) : null
      } : null;
      
      state.user = sanitizedUser;
      state.lastAuthUpdate = Date.now(); // Update timestamp when user is set
    },
    setRole: (state, action) => {
      state.role = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setLoginComplete: (state, action) => {
      state.loginComplete = action.payload;
    },
    updateUserDefaultFacility: (state, action) => {
      if (state.user) {
        // action.payload should be the complete facility object
        state.user.defaultFacility = sanitizeFacilityData(action.payload);
        state.lastAuthUpdate = Date.now(); // Update timestamp when facility changes
      }
    },
    clearAuth: (state) => {
      state.user = null;
      state.role = null;
      state.error = null;
      state.loginComplete = false;
      state.lastAuthUpdate = null;
    }
  },
  extraReducers: (builder) => {
    // Handle facilities loading as part of auth flow
    builder
      .addCase(fetchUserFacilities.fulfilled, (state) => {
        // Mark login as complete only if we have a user
        if (state.user) {
          state.loginComplete = true;
        }
      })
      .addCase(fetchUserFacilities.rejected, (state) => {
        // Still mark login as complete even if facilities fail
        if (state.user) {
          state.loginComplete = true;
        }
      })
      // Handle fetchFacilities for admin users
      .addCase(fetchFacilities.fulfilled, (state) => {
        // Mark login as complete only if we have a user
        if (state.user) {
          state.loginComplete = true;
        }
      })
      .addCase(fetchFacilities.rejected, (state) => {
        // Still mark login as complete even if facilities fail
        if (state.user) {
          state.loginComplete = true;
        }
      });
  }
});

export const { 
  setUser, 
  setRole, 
  setLoading, 
  setError, 
  setLoginComplete, 
  updateUserDefaultFacility,
  clearAuth 
} = authSlice.actions;

export const selectUser = (state) => state.auth.user;
export const selectRole = (state) => state.auth.role;
export const selectLoading = (state) => state.auth.loading;
export const selectError = (state) => state.auth.error;
export const selectLoginComplete = (state) => state.auth.loginComplete;
export const selectLastAuthUpdate = (state) => state.auth.lastAuthUpdate;

// Utility function to check if auth has expired (24 hours by default)
export const isAuthExpired = (lastAuthUpdate, expirationHours = 24) => {
  if (!lastAuthUpdate) return true;
  const now = Date.now();
  const expiration = lastAuthUpdate + (expirationHours * 60 * 60 * 1000);
  return now > expiration;
};

// Selector to check if current auth is expired
export const selectIsAuthExpired = (state) => {
  return isAuthExpired(state.auth.lastAuthUpdate);
};

export default authSlice.reducer;
